<script>
  import { onMount } from "svelte";
  import { Circle } from "svelte-loading-spinners";
  import MediaQuery from "svelte-media-query";
  import { browser } from "../stores";
  import ArrowLink from "../components/Common/ArrowLink.svelte";
  import getRandom from "../helpers/getRandom";
  import Header from "../components/Common/Header.svelte";
  import Error from "../components/Common/Error.svelte";
  import SectorPreviewGrid from "../components/Sectors/SectorPreviewGrid.svelte";
  import subscribeToNewsletter from "../helpers/subscribeToNewsletter";
  import Success from "../components/Common/Success.svelte";

  export let slug;

  const displayTitle =
    WP.sectorIdMap.find((s) => s.slug === slug)?.title || "Title";
  let sector = WP.sectorIdMap.find((s) => s.slug === slug);

  // State
  let email = "",
    subscribed = false,
    subscriptionLoading = false,
    subscriptionError = false;

  let htmlContent = "<div></div>";
  let allSectors = WP.sectorIdMap.filter((sec) => sec.slug !== slug);
  let sectors = allSectors.length > 3 ? getRandom(allSectors, 3) : allSectors;
  let loading = false;
  let apiError = null;

  // Subscription Handler
  const handleSubscription = async (email) => {
    subscriptionLoading = true;
    const response = await subscribeToNewsletter(email);

    if (response.code && response.code != 200) {
      subscriptionError = true;
      setTimeout(() => {
        subscriptionError = false;
      }, 5000);
      console.error(response);
    } else {
      subscribed = true;
    }

    subscriptionLoading = false;
  };

  // GET API Call to get Sector content
  onMount(() => {
    loading = true;
    fetch(`/wp-json/single-sector/${sector.id}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((res) => {
        const { content } = res;
        if (content?.length) {
          htmlContent = content;
        }
        loading = false;
      })
      .catch(() => {
        apiError = "Could not load Sector content";
        loading = false;
      });

    if (browser.name === "ie") {
      window.scrollTo(0, 0);
    }
  });
</script>

{#if loading}
  <div id="loading-page">
    <div id="loading-wrapper">
      <Circle size="100" color="#4a4a4a" unit="px" />
    </div>
  </div>
{:else if apiError}
  <div id="error-page">
    <p>{apiError}</p>
  </div>
{:else}
  <MediaQuery query="(max-width: 1200px)" let:matches>
    <Header>
      <div class="top">
        <div class="title">
          <div class="sector-crumbs">
            <ArrowLink href="/reports" routing={true} text="Reports" />
            <ArrowLink href="/reports/sectors" routing={true} text="Sectors" />
          </div>
          <h1>{displayTitle}</h1>
          <p>{sector.description}</p>
        </div>

        {#if !matches}
          <div class="newsletter-signup">
            {#if subscriptionError}
              <Error>There was an error processing your subscription.</Error>
            {:else if subscribed}
              <Success>
                Thanks for signing up for our reports subscription.
              </Success>
              <p style="margin-bottom: 0; padding-bottom: 0;">
                You should receive an email to activate your account in a few
                minutes. If not, be sure to check your spam folder just in case.
                Once you activate your account you will be able to change your
                subscription preferences and/or unsubscribe at any time.
              </p>
            {:else}
              <p>
                Get the latest {displayTitle}<br />reports straight to your
                inbox.
              </p>
              <form on:submit|preventDefault={() => handleSubscription(email)}>
                <input
                  type="email"
                  bind:value={email}
                  placeholder="Email Address"
                  disabled={subscriptionLoading}
                />
                <button
                  class="button"
                  type="submit"
                  disabled={subscriptionLoading || !email.length}
                >
                  {#if subscriptionLoading}
                    <Circle size="20" color="#fff" unit="px" />
                  {:else}
                    Subscribe
                  {/if}
                </button>
              </form>
            {/if}
          </div>
        {/if}
      </div>
    </Header>
    <hr id="sector-top-border" />

    {#if matches}
      <div class="bar">
        <div class="newsletter-signup">
          {#if subscriptionError}
            <Error>There was an error processing your subscription.</Error>
          {:else if subscribed}
            <Success>
              Thanks for signing up for our reports subscription.
            </Success>
            <p>
              You should receive an email to activate your account in a few
              minutes. If not, be sure to check your spam folder just in case.
              Once you activate your account you will be able to change your
              subscription preferences and/or unsubscribe at any time.
            </p>
          {:else}
            <p>
              Get the latest {displayTitle} reports straight to your inbox.
            </p>
            <form on:submit|preventDefault={() => handleSubscription(email)}>
              <input
                type="email"
                bind:value={email}
                placeholder="Email Address"
                disabled={subscriptionLoading}
              />
              <button
                class="button"
                type="submit"
                disabled={subscriptionLoading || !email.length}
              >
                {#if subscriptionLoading}
                  <Circle size="20" color="#fff" unit="px" />
                {:else}
                  Subscribe
                {/if}
              </button>
            </form>
          {/if}
        </div>
        <div class="report-search">
          <p>Search {displayTitle} Reports</p>

          <a
            class="button"
            href={`/reports/search?sectorIds=${sector.id}&sort=date`}
          >
            Go
          </a>
        </div>
      </div>
    {/if}

    <div class="container">
      <main>
        <div id="sector-content">
          {@html htmlContent}
        </div>
        {#if !matches}
          <div id="sector-sidebar">
            <h2>{displayTitle} Reports</h2>

            <a
              class="button"
              href={`/reports/search?sectorIds=${sector.id}&sort=date`}
            >
              Find Reports
            </a>
          </div>
        {/if}
      </main>
    </div>
  </MediaQuery>
{/if}

<footer>
  <div class="container">
    <h2>Explore Other Sectors</h2>
    <SectorPreviewGrid {sectors} mode="explore" />
  </div>
</footer>

<style>
  .top {
    display: flex;
    justify-content: space-between;
  }
  .sector-crumbs {
    display: flex;
  }
  .sector-crumbs :global(a) {
    margin-right: 24px;
  }

  .title {
    padding-right: 48px;
  }

  .newsletter-signup {
    flex-basis: 537px;
    margin-top: 27px;
  }
  form {
    display: flex;
    align-items: stretch;
  }

  .newsletter-signup p {
    margin-bottom: 18px;
  }

  .newsletter-signup input {
    width: 302px;
    border: none;
    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    padding: 20px;
  }

  .newsletter-signup button {
    margin-left: 48px;
    width: 187px;
    background: #d07743;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    color: #ffffff;
  }

  #sector-top-border {
    height: 2px;
    background: #ebe9e9;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
  }

  main {
    display: flex;
    padding-top: 40px;
  }

  #sector-content {
    padding-right: 10%;
  }

  #sector-sidebar {
    flex-basis: 420px;
    flex-shrink: 0;
    padding-top: 48px;
  }

  #sector-sidebar h2 {
    font-family: Georgia;
    font-weight: normal;
    font-size: 32px;
    line-height: 40px;
    display: flex;
    align-items: center;
    color: #0e4369;
    margin-bottom: 20px;
  }

  footer {
    padding-top: 80px;
  }

  #loading-page,
  #error-page {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    height: 100%;
    min-height: 50vh;
    position: relative;
  }

  #loading-wrapper {
    margin: auto;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  #error-page p {
    text-align: center;
    color: #ea3009;
  }

  @media (max-width: 1200px) {
    .top {
      flex-direction: column;
    }

    .bar {
      padding: 30px;
      background: #ebe9e9;
      display: flex;
      align-items: center;
    }

    .newsletter-signup {
      margin-top: 0;
      width: calc(60% + 50px);
      padding-right: 50px;
      margin-right: 50px;
      border-right: 2px solid #9b9b9b;
    }

    .report-search p {
      margin-bottom: 18px;
    }

    .report-search .button {
      height: 60px;
    }

    #sector-top-border {
      display: none;
    }

    #sector-content {
      padding-right: 0;
      margin: 0 auto;
      max-width: 680px;
    }
  }

  @media (max-width: 900px) {
    .bar {
      flex-direction: column;
      align-items: flex-start;
    }

    .newsletter-signup {
      width: 100%;
      flex-basis: auto;
      margin-right: 0;
      border-right: 0;
      border-bottom: 2px solid #9b9b9b;
      padding-right: 0;
      padding-bottom: 30px;
      margin-bottom: 18px;
    }

    .report-search {
      width: 100%;
    }
  }

  @media (max-width: 530px) {
    form {
      flex-direction: column;
    }
    form button {
      height: 60px;
      margin-top: 18px;
      margin-left: 0 !important; /*shame*/
    }

    .bar input,
    .bar button,
    .bar .button {
      width: 100%;
    }
  }
</style>
